/**
 * Add-tile validation (S6). Two layers with different jobs:
 *  - `submitBlocker` — structural gates (type picked, connection + SQL for
 *    query tiles) that disable the submit button, with the reason as tooltip.
 *  - `configShapeHint` — soft, actionable shape hints (chart needs category +
 *    numeric; pivot needs two categories + numeric; …). NEVER blocks: an
 *    empty result with valid shape is a healthy monitoring state.
 */
import type { ColumnTypeInfo } from '../../../charts/types/chartTypes';
import type { AddTileFormValues } from './addTileFormModel';

export function submitBlocker(values: AddTileFormValues, sql: string): string | null {
  if (!values.tileType) return 'Pick a tile type first';
  if (values.tileType === 'note') return null;
  if (!values.connectionId) return 'Pick a connection';
  if (!sql.trim()) return 'Write the tile query';
  return null;
}

export function configShapeHint(
  values: AddTileFormValues,
  columnInfos: ColumnTypeInfo[],
  previewRan: boolean,
  /** Result-row keys detected as geometry (spatialShapeDetect) — map hint. */
  spatialColumnNames: string[] = [],
): string | null {
  if (!previewRan || columnInfos.length === 0) return null;
  const numeric = columnInfos.filter((c) => c.category === 'numeric').length;
  const categorical = columnInfos.filter(
    (c) => c.category === 'string' || c.category === 'boolean' || c.category === 'date',
  ).length;
  switch (values.tileType) {
    case 'chart':
      if (numeric === 0 || categorical === 0) {
        return 'Charts need a category or time column plus a numeric value — this result is missing one. A table shows it as-is.';
      }
      if (!values.chartX || !values.chartY.some((y) => y.column)) {
        return 'Pick the category (X) and at least one value (Y) column.';
      }
      return null;
    case 'stat':
      if (numeric === 0) {
        return 'Stats read a numeric column — this result has none. A table shows it as-is.';
      }
      return null;
    case 'pivot': {
      const cats = columnInfos.filter(
        (c) => c.category === 'string' || c.category === 'boolean',
      ).length;
      if (cats < 2 || numeric === 0) {
        return 'Pivots need two category columns and a numeric value — this result is missing some.';
      }
      if (!values.pivotRow || !values.pivotColumn || !values.pivotValue) {
        return 'Pick the row, column and value columns for the pivot.';
      }
      return null;
    }
    case 'map':
      if (spatialColumnNames.length === 0) {
        return 'Maps need a geometry column — none was detected in this result. A table shows it as-is.';
      }
      return null;
    default:
      return null;
  }
}
